/**
 * CheckInDialog Component
 * Confirmation dialog for check-in action
 *
 * @version 2025.1.0
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Event as EventIcon,
  MedicalServices as ExamIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { ReceptionAppointment, formatTime } from '../../../types/reception';

interface CheckInDialogProps {
  open: boolean;
  appointment: ReceptionAppointment | null;
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

const InfoRow: React.FC<InfoRowProps> = ({ icon, label, value }) => (
  <Box display="flex" alignItems="center" gap={2} py={1}>
    <Box color="action.active">{icon}</Box>
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight="medium">
        {value}
      </Typography>
    </Box>
  </Box>
);

export const CheckInDialog: React.FC<CheckInDialogProps> = ({
  open,
  appointment,
  loading = false,
  onConfirm,
  onClose,
}) => {
  if (!appointment) return null;

  const formattedTime = formatTime(appointment.hour, appointment.minute, appointment.period);

  // Check period mismatch for arrival-order appointments
  const currentPeriod = new Date().getHours() < 12 ? 'morning' : 'afternoon';
  const hasPeriodMismatch = appointment.period && !appointment.hour && appointment.period !== currentPeriod;
  const periodMismatchMessage = hasPeriodMismatch
    ? appointment.period === 'morning'
      ? 'Este atendimento está agendado para o período da manhã, mas o check-in está sendo realizado no período da tarde.'
      : 'Este atendimento está agendado para o período da tarde, mas o check-in está sendo realizado no período da manhã.'
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Confirmar Check-in</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Confirme os dados do paciente antes de realizar o check-in:
        </Typography>

        <Divider />

        <Box py={1}>
          <InfoRow
            icon={<PersonIcon />}
            label="Funcionário"
            value={appointment.employeeName}
          />
          <InfoRow
            icon={<BusinessIcon />}
            label="Empresa"
            value={appointment.companyName}
          />
          <InfoRow
            icon={<ExamIcon />}
            label="Tipo de Exame"
            value={appointment.examType}
          />
          <InfoRow
            icon={<EventIcon />}
            label="Horário Agendado"
            value={formattedTime}
          />
        </Box>

        {periodMismatchMessage && (
          <Alert severity="warning" icon={<WarningIcon />} sx={{ my: 1 }}>
            {periodMismatchMessage}
          </Alert>
        )}

        <Divider />

        <Typography variant="body2" color="text.secondary" mt={2}>
          Ao confirmar, o paciente receberá um número de senha e será adicionado
          à fila de atendimento.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Processando...' : 'Confirmar Check-in'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CheckInDialog;
